import { supabase } from "@/lib/supabase/client";

export type FundStructure = "limited_partnership" | "llc" | "trust" | "plc" | "sicav" | "other";

export type FundStrategy =
  | "buyout"
  | "growth"
  | "venture"
  | "credit"
  | "real_estate"
  | "infrastructure"
  | "fund_of_funds"
  | "other";

export type FundStatus = "forming" | "fundraising" | "active" | "harvesting" | "wound_down";

export interface Fund {
  id: string;
  tenant_id: string;
  name: string;
  short_name: string | null;
  vintage_year: number | null;
  fund_size_usd: number | null;
  domicile: string | null;
  structure: FundStructure | null;
  strategy: FundStrategy | null;
  status: FundStatus;
  created_at: string;
  updated_at: string;
}

export interface CreateFundInput {
  tenant_id: string;
  name: string;
  short_name?: string | null;
  vintage_year?: number | null;
  fund_size_usd?: number | null;
  domicile?: string | null;
  structure?: FundStructure | null;
  strategy?: FundStrategy | null;
  status?: FundStatus;
}

export async function listFunds(tenantId: string): Promise<Fund[]> {
  const { data, error } = await supabase
    .from("funds")
    .select(
      "id, tenant_id, name, short_name, vintage_year, fund_size_usd, domicile, structure, strategy, status, created_at, updated_at",
    )
    .eq("tenant_id", tenantId)
    .order("vintage_year", { ascending: false, nullsFirst: false })
    .order("name", { ascending: true });
  if (error) throw new Error(`listFunds: ${error.message}`);
  return data ?? [];
}

export async function createFund(input: CreateFundInput): Promise<Fund> {
  const { data, error } = await supabase
    .from("funds")
    .insert(input)
    .select(
      "id, tenant_id, name, short_name, vintage_year, fund_size_usd, domicile, structure, strategy, status, created_at, updated_at",
    )
    .single();
  if (error) throw new Error(`createFund: ${error.message}`);
  return data;
}

export interface UpdateFundInput {
  name?: string;
  short_name?: string | null;
  vintage_year?: number | null;
  fund_size_usd?: number | null;
  domicile?: string | null;
  structure?: FundStructure | null;
  strategy?: FundStrategy | null;
  status?: FundStatus;
}

export async function getFund(id: string): Promise<Fund> {
  const { data, error } = await supabase
    .from("funds")
    .select(
      "id, tenant_id, name, short_name, vintage_year, fund_size_usd, domicile, structure, strategy, status, created_at, updated_at",
    )
    .eq("id", id)
    .single();
  if (error) throw new Error(`getFund: ${error.message}`);
  return data;
}

export async function updateFund(id: string, input: UpdateFundInput): Promise<Fund> {
  const { data, error } = await supabase
    .from("funds")
    .update(input)
    .eq("id", id)
    .select(
      "id, tenant_id, name, short_name, vintage_year, fund_size_usd, domicile, structure, strategy, status, created_at, updated_at",
    )
    .single();
  if (error) throw new Error(`updateFund: ${error.message}`);
  return data;
}

export async function deleteFund(id: string): Promise<void> {
  const { error } = await supabase.from("funds").delete().eq("id", id);
  if (error) throw new Error(`deleteFund: ${error.message}`);
}
